import { Component, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthorizationService } from '@app/core/auth/authorization.service';
import { IsBlvDirective } from '@app/core/auth/is-blv.directive';
import { IsTierarztDirective } from '@app/core/auth/is-tierarzt.directive';
import { MeldungListFilterComponent } from '@app/meldung-list/meldung-list-filter/meldung-list-filter.component';
import { MeldungListTableComponent } from '@app/meldung-list/meldung-list-table/meldung-list-table.component';
import { MeldungBewilligungFilter } from '@app/shared/classes/meldung-bewilligung-filter';
import { BlvStatusId, MeldungFilter } from '@app/shared/classes/meldung-filter';
import { downloadFile } from '@app/shared/download-utils';
import { DropdownDirective } from '@app/shared/dropdown.directive';
import { BackendService } from '@app/shared/services/backend.service';
import { CryptoService } from '@app/shared/services/crypto.service';
import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { DpDatePickerModule, IDatePickerDirectiveConfig } from 'ng2-date-picker';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { rxResource } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-meldung-list',
  templateUrl: './meldung-list.component.html',
  imports: [
    DpDatePickerModule,
    DropdownDirective,
    IsBlvDirective,
    IsTierarztDirective,
    MeldungListFilterComponent,
    MeldungListTableComponent,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
  ]
})
export class MeldungListComponent implements OnInit {
  private readonly backendService = inject(BackendService);
  private readonly cryptoService = inject(CryptoService);
  private readonly authorizationService = inject(AuthorizationService);

  protected readonly classFreitext   : string = this.authorizationService.isBlv() ? 'col-sm-7.5 mb-0' : 'col-sm-12 mb-0';
  protected readonly classLandStatus : string = this.authorizationService.isBlv() ? 'col-sm-2.5 mb-0' : 'col-sm-4 mb-0';

  protected readonly laender = rxResource({ defaultValue: [], stream: () => this.backendService.listVerwendeteLaender() });
  protected readonly statusList = rxResource({ defaultValue: [], stream: () => this.backendService.listStatus() });
  protected readonly filterComponent = viewChild.required<ElementRef>('filterComponent');

  protected readonly filter = signal(new MeldungFilter());
  protected bewilligung = new MeldungBewilligungFilter();
  protected totalMeldungen = 0;

  protected readonly datumVonBis     : IDatePickerDirectiveConfig = {
    firstDayOfWeek: "mo",
    format: "DD.MM.YYYY",
    disableKeypress: true,
    max: dayjs()
  };

  protected readonly filterForm = new UntypedFormGroup({
    freitext      : new UntypedFormControl(this.filter().freitext),
    land          : new UntypedFormControl(this.filter().land),
    status        : new UntypedFormControl(this.filter().status),
    offeneAnfrage : new UntypedFormControl(this.filter().offeneAnfrage),
    datumVon      : new UntypedFormControl(this.filter().datumVon),
    datumBis      : new UntypedFormControl(this.filter().datumBis),
    blvStatus     : new UntypedFormControl(this.filter().blvStatus)
  });

  ngOnInit(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged()
      )
      .subscribe(values => this.storeFilter(MeldungFilter.fromJSON(values)) );

    let filterCiphertext = localStorage.getItem('filter');
    if (filterCiphertext) {
      this.cryptoService.decrypt(filterCiphertext).then(filterPlaintext => {
        this.filter.set(MeldungFilter.fromJSON(filterPlaintext));
        this.bewilligung = new MeldungBewilligungFilter(this.filter().bewilligung);
        this.filterForm.setValue({
          freitext      : this.filter().freitext,
          land          : this.filter().land,
          status        : this.filter().status,
          offeneAnfrage : this.filter().offeneAnfrage,
          datumVon      : this.filter().datumVon,
          datumBis      : this.filter().datumBis,
          blvStatus     : this.filter().blvStatus
        });
      });
    }
  }

  protected clearValue(name: string): void {
    this.filterForm?.patchValue({ [name]: '' });
  }

  protected resetFilter(): void {
    this.bewilligung = new MeldungBewilligungFilter();
    this.filterForm?.setValue({
      freitext      : '',
      land          : 0,
      status        : 0,
      offeneAnfrage : 0,
      datumVon      : '',
      datumBis      : '',
      blvStatus     : 0
    });
  }

  protected downloadCsv(): void {
    this.backendService.getMeldungenCsv(this.filter())
      .subscribe({
        next  : downloadFile,
        error : console.error
      });
  }

  protected downloadXls(): void {
    this.backendService.getMeldungenXls(this.filter())
      .subscribe({
        next  : downloadFile,
        error : console.error
      });
  }

  protected downloadZoll(): void {
    const zollFilter = new MeldungFilter({
      blvStatus : BlvStatusId.VOLLSTAENDIG,
      datumVon  : dayjs().subtract(6, 'months').format('DD.MM.YYYY')
    });

    this.backendService.getDatenauszugZoll(zollFilter)
      .subscribe({
        next  : downloadFile,
        error : console.error
      });
  }

  protected erweiterterFilterAktiv(): boolean {
    return this.bewilligung.anyAktiv();
  }

  protected erweiterterFilterAnzeigen(): void {
    Swal.fire({
      html     : this.filterComponent().nativeElement,
      width    : 'auto',
      didClose : () => this.storeFilter(new MeldungFilter(this.filter()))
    });
  }

  private storeFilter(filter: MeldungFilter): void {
    filter.bewilligung = this.bewilligung;
    this.filter.set(filter);
    let filterPlaintext = JSON.stringify(this.filter());
    this.cryptoService.encrypt(filterPlaintext).then(filterCiphertext => {
      localStorage.setItem('filter', filterCiphertext);
    });
  }
}
